import React, { createContext, useContext, useState, useEffect } from 'react';
import { BRAND, STORIES, LEGACY_CONTENT, BUNDLES } from '../constants';
import { get, set, keys, del } from 'idb-keyval';
import { useUser } from './UserContext';
import { db, storage, handleFirestoreError, OperationType } from '../firebase';
import { doc, setDoc, updateDoc, collection, getDocs, onSnapshot, deleteDoc, addDoc, query, where, orderBy } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

interface Product {
  id: string;
  name: string;
  price: number;
  description: string;
  icon: string;
  image: string;
  video?: string;
  stock: number;
  isPreOrder: boolean;
  items: string[];
  category?: string;
  isVessel?: boolean;
  blueprint?: string;
  tier?: string;
  interval?: string;
  gallery?: Array<{
    id: string;
    type: 'image' | 'video';
    url: string;
    hasSound?: boolean;
  }>;
}

interface ContentBlock {
  id?: string;
  type: 'text' | 'image' | 'video' | 'map';
  value: string;
  caption?: string;
  hasSound?: boolean;
  coords?: { lat: number; lng: number };
}

interface Story {
  id: string;
  author: string;
  role: string;
  title: string;
  excerpt: string;
  content: string;
  contentBlocks?: ContentBlock[];
  image: string;
  date: string;
  isFeatured: boolean;
  published?: boolean;
  category?: string;
  location?: string;
  coords?: { lat: number; lng: number };
}

export interface ExpeditionStop {
  id: string;
  day: number;
  title: string;
  location: string;
  description: string;
  image: string;
  coords: { x: number; y: number; lat?: number; lng?: number };
  distance?: string;
  wind?: string;
  swell?: string;
}

export interface ExpeditionSlot {
  id?: string;
  title?: string;
  date: string;
  time: string;
  capacity: number;
  booked: number;
  status: 'available' | 'full' | 'cancelled';
  type: 'night-raid' | 'coaching' | 'team-building' | 'baja' | 'group-lesson' | 'lake-crossing' | 'event' | 'corporate' | 'expedition';
  price: number;
  location?: string;
  exactMeetingLocation?: string;
}

export interface ExpeditionBooking {
  id: string;
  slotId: string;
  userId: string;
  userEmail: string;
  userName: string;
  userPhone: string;
  waiverAccepted: boolean;
  requiresGear?: boolean;
  timestamp: string;
  createdAt?: string;
  date?: string;
  time?: string;
  status: 'confirmed' | 'cancelled' | 'waitlist' | 'pending';
  type: string;
  serviceName: string;
  instructions?: {
    expect?: string;
    bring?: string;
    meet?: string;
    clothes?: string;
  };
}

export interface Order {
  id: string;
  userId: string;
  userPhone?: string;
  userEmail: string;
  userName: string;
  items: any[];
  total: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'paid' | 'UNFULFILLED_PREORDER' | 'IN_PRODUCTION';
  fulfillmentStatus?: 'UNFULFILLED_PREORDER' | 'IN_PRODUCTION' | 'DELIVERED';
  batchId?: string;
  deferredRevenue?: number;
  hardwareRevenue?: number;
  serviceRevenue?: number;
  trueLandedCost?: number;
  timestamp: string;
  createdAt?: string;
  type?: 'shop_order' | 'vessel_reservation' | 'membership_purchase' | 'experience_booking';
  isVessel?: boolean;
  shippingAddress?: {
    name: string;
    street: string;
    city: string;
    postalCode: string;
    country?: string;
  };
  tax?: number;
  shipping?: number;
  subtotal?: number;
  isPromotionalAdjustment?: boolean;
  paymentMethod?: string;
  shippingQuoteAmount?: number;
  shippingQuoteGenerated?: boolean;
  shippingQuoteInvoiceUrl?: string;
  shippingQuotePaid?: boolean;
  localPickupRequested?: boolean;
  balanceInvoiceStatus?: string;
  balanceInvoiceUrl?: string;
  balanceInvoiceId?: string;
  balanceInvoiceSentAt?: string;
}

export interface FinancialSettings {
  taxRate: number; // e.g., 13 for 13%
  shippingRate: number; // flat rate
}

export interface MembershipPlan {
  id: string;
  name: string;
  price: number;
  features: string[];
  description?: string;
  interval: string;
  tier: string;
  icon: string;
}

export interface CoachingPlan {
  id: string;
  type: string;
  title: string;
  description: string;
  price: number;
  interval?: string;
  details: string;
  features: string[];
  icon: string;
}

interface TrackingConfig {
  mapShareUrl: string;
  liveTrackUrl: string;
  kmlLoaderUrl: string;
  rawKmlUrl: string;
  enabled: boolean;
}

interface BrandContextType {
  assets: {
    logo: string;
    heroVideo: string;
    nightRaidVideo: string;
    boardImage: string;
    detailImage: string;
    distressedAesthetic: boolean;
    heroHeadline?: string;
    heroSubtitle?: string;
    aboutImage?: string;
    footerLogo?: string;
    membershipHero?: string;
    shopHero?: string;
    nightRaidHeroVideo?: string;
    vesselVideo?: string;
    vesselGallery?: string[];
    daySafetyVideo?: string;
    nightSafetyVideo?: string;
    vesselBackground?: string;
    heroImage?: string;
    nightRaidCardImage?: string;
    nightRaidHeroImage?: string;
    coachingCardImage?: string;
    teamBuildingCardImage?: string;
    giftCardImage?: string;
    garminData1?: string;
    garminData2?: string;
    garminData3?: string;
  };
  content: {
    brandName: string;
    tagline: string;
    description: string;
    heroHeadline: string;
    heroSubtitle: string;
    legacyTitle: string;
    legacyBody: string;
    comparisonTitle: string;
    comparisonSubtitle: string;
    vesselName: string;
    vesselDimensions: string;
    vesselFeatures: string;
    vesselBlueprint?: string;
    vesselVideos: string;
    aboutUsText: string;
    aboutUsMission: string;
    dreamLabel: string;
    dreamTitle: string;
    dreamSubtitle: string;
    dreamQuote: string;
    cultureTitle: string;
    cultureDesc: string;
    communityTitle: string;
    communityDesc: string;
    missionTitle: string;
    missionDesc: string;
    logLabel: string;
    logTitle: string;
    logSubtitle: string;
    logQuote: string;
    vesselLabel: string;
    vesselTitle: string;
    vesselSubtitle: string;
    vesselDescription: string;
    configuratorLabel: string;
    configuratorTitle: string;
    configuratorSubtitle: string;
    nightRaidLabel: string;
    nightRaidTitle: string;
    nightRaidSubtitle: string;
    nightRaidQuote: string;
    nightRaidLocation: string;
    nightRaidSchedule: string;
    nightRaidGroupSize: string;
    voyagerLabel: string;
    voyagerTitle: string;
    voyagerSubtitle: string;
    voyagerDescription: string;
    calendarLabel: string;
    calendarTitle: string;
    calendarSubtitle: string;
    calendarDescription: string;
    syndicateLabel: string;
    syndicateTitle: string;
    syndicateSubtitle: string;
    syndicateQuote: string;
    syndicateFeature1Title: string;
    syndicateFeature1Desc: string;
    syndicateFeature2Title: string;
    syndicateFeature2Desc: string;
    syndicateFeature3Title: string;
    syndicateFeature3Desc: string;
    syndicateFeature4Title: string;
    syndicateFeature4Desc: string;
    shippingDate: string;
    pricingNote: string;
    expeditionsLabel: string;
    expeditionsTitle: string;
    expeditionsDescription: string;
    garmin1Title: string;
    garmin1Desc: string;
    garmin1Sector: string;
    garmin2Title: string;
    garmin2Desc: string;
    garmin2Sector: string;
    garmin3Title: string;
    garmin3Desc: string;
    garmin3Sector: string;
  };
  products: Product[];
  stories: Story[];
  founders: { id: string; name: string; role: string; image: string; quote: string; }[];
  expeditionSlots: ExpeditionSlot[];
  loadingSlots: boolean;
  userBookings: ExpeditionBooking[];
  orders: Order[];
  adminBookings?: ExpeditionBooking[];
  adminOrders?: Order[];
  membershipPlans: MembershipPlan[];
  mission: {
    goal: number;
    current: number;
    label: string;
    headline: string;
    subheadline: string;
    refundPolicy: string;
  };
  explorerTV: any[];
  nightGallery: any[];
  mediaLibrary: any[];
  expeditionStops: ExpeditionStop[];
  coachingPlans: CoachingPlan[];
  trackingConfig: TrackingConfig;
  financialSettings: FinancialSettings;
  isEditMode: boolean;
  currentUser: any;
  isAuthReady: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  updateAsset: (key: string, value: string | boolean) => Promise<void>;
  updateContent: (key: string, value: string) => Promise<void>;
  updateProduct: (id: string, updates: Partial<Product>) => Promise<void>;
  addCoachingPlan: (plan: Partial<CoachingPlan>) => Promise<void>;
  updateCoachingPlan: (id: string, plan: Partial<CoachingPlan>) => Promise<void>;
  removeCoachingPlan: (id: string) => Promise<void>;
  addProduct: (product: Product) => Promise<void>;
  removeProduct: (id: string) => Promise<void>;
  updateStory: (id: string, updates: Partial<Story>) => Promise<void>;
  addStory: (story: Story) => Promise<void>;
  removeStory: (id: string) => Promise<void>;
  updateFounder: (id: string, updates: any) => Promise<void>;
  addFounder: (founder: any) => Promise<void>;
  removeFounder: (id: string) => Promise<void>;
  updateMembershipPlan: (id: string, updates: Partial<MembershipPlan>) => Promise<void>;
  addMembershipPlan: (plan: MembershipPlan) => Promise<void>;
  removeMembershipPlan: (id: string) => Promise<void>;
  updateMission: (updates: Partial<BrandContextType['mission']>) => Promise<void>;
  updateExplorerTV: (id: string, updates: any) => Promise<void>;
  addExplorerTV: (video: any) => Promise<void>;
  removeExplorerTV: (id: string) => Promise<void>;
  updateNightGallery: (id: string, updates: any) => Promise<void>;
  addNightGallery: (item: any) => Promise<void>;
  removeNightGallery: (id: string) => Promise<void>;
  addMediaLibrary: (item: any) => Promise<void>;
  removeMediaLibrary: (id: string) => Promise<void>;
  updateExpeditionStop: (id: string, updates: any) => Promise<void>;
  addExpeditionStop: (stop: any) => Promise<void>;
  removeExpeditionStop: (id: string) => Promise<void>;
  bulkUpdateContent: (updates: Record<string, string>) => Promise<void>;
  syncStatuses: Record<string, any>;
  updateSyncStatus: (section: string, status: string) => Promise<void>;
  updateTrackingConfig: (updates: Partial<TrackingConfig>) => Promise<void>;
  updateFinancialSettings: (updates: Partial<FinancialSettings>) => Promise<void>;
  addExpeditionSlot: (slot: ExpeditionSlot) => Promise<void>;
  updateExpeditionSlot: (id: string, updates: Partial<ExpeditionSlot>) => Promise<void>;
  removeExpeditionSlot: (id: string) => Promise<void>;
  bookExpedition: (slotId: string, details: { name: string, email: string, phone: string, waiverAccepted: boolean, requiresGear?: boolean, intensityLevel?: string }) => Promise<void>;
  cancelBooking: (bookingId: string) => Promise<void>;
  addOrder: (order: Omit<Order, 'id' | 'timestamp' | 'status'>) => Promise<void>;
  updateOrder: (id: string, updates: Partial<Order>) => Promise<void>;
  removeOrder: (id: string) => Promise<void>;
  selectedConfiguration: Product | null;
  setSelectedConfiguration: (product: Product | null) => void;
  uploadFile: (file: File, path: string, onProgress?: (progress: number) => void) => Promise<string>;
  isUploading: boolean;
  resetToDefaults: () => void;
  clearLocalMedia: () => Promise<void>;
  toggleEditMode: () => void;
  resolveMediaUrl: (url: string) => string | undefined;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

// Helper to load from localStorage
const load = (key: string, defaultValue: any) => {
  const saved = localStorage.getItem(key);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      if (typeof parsed === 'object' && !Array.isArray(parsed) && parsed !== null) {
        return { ...defaultValue, ...parsed };
      }
      return parsed;
    } catch (e) {
      return defaultValue;
    }
  }
  return defaultValue;
};

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const { user: currentUser, profile, loading: authLoading, isAdmin } = useUser();
  const [isEditMode, setIsEditMode] = useState(false);
  const isAuthReady = !authLoading;

  const [assets, setAssets] = useState<BrandContextType['assets']>(() => load('ows_assets', {
    logo: BRAND.logo,
    heroVideo: BRAND.heroVideo,
    nightRaidVideo: 'https://cdn.pixabay.com/video/2023/10/24/186358-877969181_large.mp4',
    boardImage: "https://storage.googleapis.com/static.aistudio.google.com/content/f035faa5-7581-4756-94ff-53e353d7afee/input_file_2.png",
    detailImage: "https://storage.googleapis.com/static.aistudio.google.com/content/f035faa5-7581-4756-94ff-53e353d7afee/input_file_3.png",
    distressedAesthetic: false,
    heroHeadline: "ENDURANCE",
    heroSubtitle: "Beyond the shoreline. Beyond the limit.",
    aboutImage: "https://picsum.photos/seed/ows-about/1200/800",
    footerLogo: BRAND.logo,
    membershipHero: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&q=80&w=1920", // Professional diving/expedition shot
    shopHero: "https://picsum.photos/seed/ows-shop/1920/1080",
    nightRaidHeroVideo: "https://cdn.pixabay.com/video/2023/10/24/186358-877969181_large.mp4",
    nightRaidHeroImage: "https://images.unsplash.com/photo-1517021897933-0e0319cfbc28?auto=format&fit=crop&q=80&w=1920",
    vesselVideo: "",
    vesselGallery: [],
    daySafetyVideo: "",
    nightSafetyVideo: "",
    vesselBackground: "https://images.unsplash.com/photo-1517021897933-0e0319cfbc28?auto=format&fit=crop&q=80&w=1200",
    nightRaidCardImage: "https://images.unsplash.com/photo-1517021897933-0e0319cfbc28?auto=format&fit=crop&q=80&w=1200",
    coachingCardImage: "https://images.unsplash.com/photo-1444491741275-3747c53c99b4?auto=format&fit=crop&q=80&w=1200",
    teamBuildingCardImage: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&q=80&w=1200",
    giftCardImage: "https://images.unsplash.com/photo-1513519245088-0e12902e5a38?auto=format&fit=crop&q=80&w=1200"
  }));

  const [content, setContent] = useState<BrandContextType['content']>(() => load('ows_content', {
    brandName: BRAND.name,
    tagline: BRAND.tagline,
    description: BRAND.description,
    heroHeadline: "JOIN THE \nSYNDICATE.",
    heroSubtitle: "The shallows are crowded. The deep is for the few. We are selecting 200 capable individuals to train as self-sufficient open-water operators. Will you be one of them?",
    legacyTitle: LEGACY_CONTENT.title,
    legacyBody: LEGACY_CONTENT.body,
    comparisonTitle: "The Syndicate vs. The Rest.",
    comparisonSubtitle: "We don't just build boards; we craft vessels for survival.",
    vesselName: "OWS EXPEDITION CX2",
    vesselDimensions: "12'6\" x 32\" x 6\"",
    vesselFeatures: JSON.stringify([
      { title: "Strategic Displacement", description: "V-hull geometry reduces drag by 40% in headwinds, allowing for sustained speed when planing boards stall.", icon: "Anchor" },
      { title: "Payload Architecture", description: "Integrated I-beam reinforcements allow for 172kg of expedition gear without compromising hull integrity or tracking.", icon: "Weight" },
      { title: "Abrasion Shield", description: "Dual military-grade PVC rails provide deep-field protection against rocky coastlines and ice-flows.", icon: "Shield" }
    ]),
    vesselBlueprint: "",
    vesselVideos: JSON.stringify([
      { title: "Hull Efficiency Race", description: "Comparing displacement efficiency against standard flat-bottom boards in 15-knot headwinds.", thumbnail: "https://picsum.photos/seed/drag-race/800/450", duration: "12:45", url: "" }
    ]),
    aboutUsText: "Open Water Syndicate (OWS) began as a private project in Baja California. Our founder, Taras, wanted a vessel that combined the portability of a paddleboard with the safety and range of an expedition kayak.",
    aboutUsMission: "We are building the world's most capable open-water exploration platform. Our goal is to democratize expedition-grade equipment, making it accessible to anyone with the spirit of adventure.",
    dreamLabel: "THE ORIGIN",
    dreamTitle: "BEYOND THE \nSHORELINE",
    dreamSubtitle: "build the resilience to conquer any horizon.",
    dreamQuote: "Anyone can play in the shallows. We are looking for the few who want to conquer the deep. We just need 300 members to train. Are you one of them?",
    cultureTitle: "THE CULTURE",
    cultureDesc: "We don't settle for calm waters. We seek the challenge of the swell and the silence of the night.",
    communityTitle: "THE COMMUNITY",
    communityDesc: "A collective of high-stakes professionals united by the discipline of the open water.",
    missionTitle: "THE MISSION",
    missionDesc: "To provide the education, the safety, and the network for serious aquatic exploration.",
    logLabel: "The Log",
    logTitle: "Expedition Reports.",
    logSubtitle: "from the edge.",
    logQuote: "\"We don't just sell boards. We live the experiences they were built for. Explore our latest journey through the Sea of Cortez.\"",
    vesselLabel: "Engineering the Vessel",
    vesselTitle: "The OWS EXPEDITION CX2.",
    vesselSubtitle: "industrial-grade survival.",
    vesselDescription: "The OWS EXPEDITION CX2 is a 32\" wide, long-range expedition vessel engineered for industrial-grade survival. Built with elite woven drop-stitch and heat-fused rails, it features a displacement hull for superior tracking and a Double Chamber for mission-critical safety. The desert-ready light tones prevent overheating, while the modular system—featuring a 9\" center fin and dual shallow-water fins—ensures coiled versatility. Stripping away marketing fluff, the OWS EXPEDITION CX2 is your mechanical insurance when the shore disappears.",
    configuratorLabel: "The Configurator",
    configuratorTitle: "Configure Your",
    configuratorSubtitle: "expedition.",
    nightRaidLabel: "Toronto Islands Night Raid",
    nightRaidTitle: "The &",
    nightRaidSubtitle: "Midnight City",
    nightRaidQuote: "\"Experience the surreal urban wilderness of the Toronto Islands lagoons under the city's neon glow. A silent journey through the heart of the metropolis.\"",
    nightRaidLocation: "Toronto Islands Inner Lagoons",
    nightRaidSchedule: "Up to 4 Hour Trips",
    nightRaidGroupSize: "$80 per person / Max 6",
    voyagerLabel: "Voyager Labs",
    voyagerTitle: "Evidence.",
    voyagerSubtitle: "Not Just Claims.",
    voyagerDescription: "We don't ask for trust; we provide the data. Watch stress tests, hull drag races, and real-world failure scenarios straight from the Syndicate's field logs.",
    calendarLabel: "Expedition Calendar",
    calendarTitle: "Available",
    calendarSubtitle: "Windows.",
    calendarDescription: "Select an available window to join the next session. Ultimate Syndicate members have unlimited free access to all sessions.",
    syndicateLabel: "The Syndicate",
    syndicateTitle: "THE SYNDICATE: INNER CIRCLE",
    syndicateSubtitle: "Alpha Cohort Strictly Capped at 200 Operators.",
    syndicateQuote: "The Syndicate is the elite inner circle of Open Water Syndicate. This one-time initiation grants you legacy access to our highest-level operations and international multi-day expeditions. Built for operators ready to push their limits, this tier secures your position on the core team, clears you for our most rigorous training protocols, and orders the first production fleet of our flagship hardware.",
    syndicateFeature1Title: "The Signature Operation",
    syndicateFeature1Desc: "Exclusive clearance for all major Lake Ontario Crossings. Reserved only for members who have passed the Deployment Standard.",
    syndicateFeature2Title: "Standard Issue Hardware",
    syndicateFeature2Desc: "All members can pre-order the OWS EXPEDITION CX2 Board (purchased separately at $999 CAD + tax), equipping you with the core team's exact loadout.",
    syndicateFeature3Title: "Unrestricted Field Access",
    syndicateFeature3Desc: "Unlimited access to all private coaching, specialized night navigation operations, group classes, and weekly meetups.",
    syndicateFeature4Title: "The Blueprint",
    syndicateFeature4Desc: "Immediate access to official training protocols and qualification path for our 2027 Gulf of California (Baja) expedition.",
    shippingDate: "June 2026",
    pricingNote: "Founders Batch Exclusive Pricing.",
    expeditionsLabel: "// OPERATIONAL SECTOR: EXPEDITIONS & TRAINING",
    expeditionsTitle: "THE PROVING GROUND: >\nMASTER THE MECHANICS OF OPEN WATER.",
    expeditionsDescription: "Move past casual paddling. From precision coiled navigation sessions to multi-day open-water lake crossings, our training prepares your mind, body, and vessel for the realities of changing wind vectors, heavy commercial wake, and deep-water endurance. If you can master the craft, you can command any horizon.",
    garmin1Title: "LAKE ONTARIO",
    garmin1Desc: "St. Catharines to Toronto 54km open-water.",
    garmin1Sector: "Lake Ontario",
    garmin2Title: "THE 20KM SPIT Expedition",
    garmin2Desc: "A raw endurance profile tracking 21.17 kilometers through active harbor shipping lanes.",
    garmin2Sector: "Leslie Spit",
    garmin3Title: "THE HARBOURFRONT INTERCEPT",
    garmin3Desc: "Urban navigation and logistics through the Toronto Harbourfront shipping channels.",
    garmin3Sector: "Inner Harbour"
  }));

  // Run a one-time migration if the user has the old default copy cached.
  useEffect(() => {
    if (content.dreamTitle === "FORGED IN THE GALE") {
      setContent(prev => {
        const updated = {
          ...prev,
          dreamLabel: "THE ORIGIN",
          dreamTitle: "BEYOND THE \nSHORELINE",
          dreamSubtitle: "build the resilience to conquer any horizon.",
          dreamQuote: "Anyone can play in the shallows. We are looking for the few who want to conquer the deep. We just need 300 members to train. Are you one of them?",
        };
        localStorage.setItem('ows_content', JSON.stringify(updated));
        return updated;
      });
      console.log('Migrated old brand content defaults.');
    }
  }, []);

  const [products, setProducts] = useState<Product[]>(BUNDLES as any);
  const [stories, setStories] = useState<Story[]>([]);
  const [founders, setFounders] = useState<any[]>([]);
  const [membershipPlans, setMembershipPlans] = useState<MembershipPlan[]>([]);
  const [coachingPlans, setCoachingPlans] = useState<CoachingPlan[]>([
    {
      id: 'private-coaching',
      type: 'private',
      title: 'Private Coaching',
      description: 'One-on-one intensive focus on stroke mechanics, wind-reading, and efficiency.',
      price: 80,
      interval: '/hr',
      details: '2hr session ($80 to start - 1 free hour)',
      features: ['1 on 1 instruction', 'Premium equipment included', 'Digital waiver and photo gallery'],
      icon: 'Compass'
    },
    {
      id: 'group-session',
      type: 'group',
      title: 'Group Session',
      description: 'Learn the fundamentals together on the water. Great for friends, families, and teams.',
      price: 80,
      interval: 'per person',
      details: '3 hours (2hrs + 1hr free)',
      features: ['6 people maximum', 'Hardware rentals available ($50)', 'Digital waiver and photo gallery'],
      icon: 'Users'
    }
  ]);
  const [mission, setMission] = useState<BrandContextType['mission']>(() => load('ows_mission', {
    goal: 200,
    current: 42,
    label: "VESSELS CLAIMED",
    headline: "THE 200-UNIT MISSION",
    subheadline: "Design the Founders Batch for the OWS EXPEDITION CX2.",
    refundPolicy: "If we hit 200 units, production starts. If not, 100% refund. No risk, just the mission."
  }));

  const [explorerTV, setExplorerTV] = useState<any[]>([]);
  const [nightGallery, setNightGallery] = useState<any[]>([]);
  const [mediaLibrary, setMediaLibrary] = useState<any[]>([]);
  const [expeditionStops, setExpeditionStops] = useState<ExpeditionStop[]>([]);
  const [syncStatuses, setSyncStatuses] = useState<Record<string, any>>({});

  const updateSyncStatus = async (section: string, status: string) => {
    try {
      const statusData = { section, status, updatedAt: new Date().toISOString() };
      setSyncStatuses(prev => ({ ...prev, [section]: statusData }));
      await setDoc(doc(db, 'syncStatuses', section), statusData);
    } catch (e) {
      console.error(`BrandContext: Failed to update sync status for ${section}`, e);
    }
  };

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'syncStatuses'), (snapshot) => {
      const statusMap = snapshot.docs.reduce((acc: any, d) => {
        acc[d.id] = d.data();
        return acc;
      }, {});
      setSyncStatuses(statusMap);
    });
    return () => unsub();
  }, []);

  const [trackingConfig, setTrackingConfig] = useState<TrackingConfig>(() => load('ows_tracking', {
    mapShareUrl: "https://share.garmin.com/OWS",
    liveTrackUrl: "https://live.garmin.com/OWS",
    kmlLoaderUrl: "https://share.garmin.com/Feed/ShareLoader/OWS",
    rawKmlUrl: "https://share.garmin.com/Feed/Share/OWS",
    enabled: true
  }));

  const [financialSettings, setFinancialSettings] = useState<FinancialSettings>(() => load('ows_financial', {
    taxRate: 13,
    shippingRate: 25
  }));
  const [expeditionSlots, setExpeditionSlots] = useState<ExpeditionSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [userBookings, setUserBookings] = useState<ExpeditionBooking[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [adminBookings, setAdminBookings] = useState<ExpeditionBooking[]>([]);
  const [adminOrders, setAdminOrders] = useState<Order[]>([]);
  const [selectedConfiguration, setSelectedConfiguration] = useState<Product | null>(null);

  // Firestore Real-time Sync
  useEffect(() => {
    const unsubs = [
      onSnapshot(collection(db, 'products'), (snapshot) => {
        const data = snapshot.docs.map(d => ({ ...d.data(), id: d.id })) as Product[];
        setProducts(data);

        const syndicateDoc = snapshot.docs.find(d => d.id === 'syndicate-ultimate');
        const syndicateData = syndicateDoc ? syndicateDoc.data() as Partial<MembershipPlan> : {};

        const corporateDoc = snapshot.docs.find(d => d.id === 'syndicate-corporate');
        const corporateData = corporateDoc ? corporateDoc.data() as Partial<MembershipPlan> : {};

        const eliteDoc = snapshot.docs.find(d => d.id === 'syndicate-elite');
        const eliteData = eliteDoc ? eliteDoc.data() as Partial<MembershipPlan> : {};

        // Use all 3 membership plans
        setMembershipPlans([
          {
            id: 'syndicate-ultimate',
            name: syndicateData.name || 'Syndicate Member',
            price: syndicateData.price ?? 300,
            interval: syndicateData.interval || 'per season',
            tier: syndicateData.tier || 'Syndicate',
            icon: syndicateData.icon || 'Shield',
            features: syndicateData.features || [
              'Free Members Group Trips (Weekly Saturday & Sunday)',
              '50% Off Private Training',
              'Official Syndicate Membership & Rank',
              'Instructor-Led Safety Sessions',
              'Endurance Progression Tracking'
            ],
          },
          {
            id: 'prospect-tier',
            name: 'Prospect',
            price: 0,
            interval: 'no ongoing commitment',
            tier: 'Prospect',
            icon: 'Users',
            features: [
              'Pay-As-You-Go for All Training',
              'Standard Rate for Expeditions',
              'Official OWS Portal Access',
              'Basic Performance Tracking',
              'Eligible for Rank Advancement'
            ],
          },
          {
            id: 'syndicate-elite',
            name: eliteData.name || 'Elite Status',
            price: eliteData.price ?? 0,
            interval: eliteData.interval || 'selection-only',
            tier: eliteData.tier || 'Elite',
            icon: eliteData.icon || 'Trophy',
            description: eliteData.description || 'Invitation Only. Requires Syndicate: Inner Circle rank before eligibility. Earned through performance and stewarded by admin selection.',
            features: eliteData.features || [
              "Requires ELITE_RANK Automation Tag",
              "Admin-Vetted Selection Only",
              "International Multi-Day Expeditions",
              "55km Night Raid Navigation missions",
              "Environment Logistics",
              "Exclusive Hardware Prototyping"
            ],
          }
        ]);
      }, (err) => console.error("BrandContext products error:", err)),
      onSnapshot(collection(db, 'blog_posts'), (snapshot) => {
        const data = snapshot.docs.map(d => ({ ...d.data(), id: d.id })) as Story[];
        setStories(data.filter(s => !['Explorer TV', 'Night Gallery', 'Media Library'].includes(s.category || '')));
        setExplorerTV(data.filter(s => s.category === 'Explorer TV'));
        setNightGallery(data.filter(s => s.category === 'Night Gallery'));
        setMediaLibrary(data.filter(s => s.category === 'Media Library'));
      }, (err) => console.error("BrandContext blog_posts error:", err)),
      onSnapshot(collection(db, 'coaching_plans'), (snapshot) => {
        if (!snapshot.empty) {
          const data = snapshot.docs.map(d => ({ ...d.data(), id: d.id })) as CoachingPlan[];
          setCoachingPlans(data);
        }
      }, (err) => console.error("BrandContext coaching_plans error:", err)),
      onSnapshot(collection(db, 'expedition_slots'), (snapshot) => {
        const slots = snapshot.docs.map(d => {
          const s = d.data() as ExpeditionSlot;
          // Enforce $80/person rate and max 6 capacity for Group Lessons
          if (s.type === 'group-lesson') {
            s.price = 80;
            s.capacity = 6;
          }
          return { ...s, id: d.id };
        }) as ExpeditionSlot[];
        setExpeditionSlots(slots);
        setLoadingSlots(false);
      }, (err) => console.error("BrandContext expedition_slots error:", err)),
      onSnapshot(collection(db, 'expedition_stops'), (snapshot) => {
        setExpeditionStops(snapshot.docs.map(d => ({ ...d.data(), id: d.id })) as ExpeditionStop[]);
      }, (err) => console.error("BrandContext expedition_stops error:", err)),
      onSnapshot(collection(db, 'brand_content'), (snapshot) => {
        console.log('BrandContext: Received brand_content snapshot');
        const contentMap: any = {};
        snapshot.docs.forEach(d => {
          contentMap[d.id] = d.data().value;
        });

        if (contentMap.assets) {
          setAssets(prev => {
            // Only update if there's an actual change to prevent unnecessary re-renders
            const hasChanged = JSON.stringify(prev) !== JSON.stringify({ ...prev, ...contentMap.assets });
            if (!hasChanged) return prev;

            console.log('BrandContext: Merging assets from Firestore', contentMap.assets);
            const merged = { ...prev, ...contentMap.assets };
            // Sync back to local storage to ensure persistence
            localStorage.setItem('ows_assets', JSON.stringify(merged));
            return merged;
          });
        }
        if (contentMap.mission) {
          setMission(prev => {
            const hasChanged = JSON.stringify(prev) !== JSON.stringify({ ...prev, ...contentMap.mission });
            if (!hasChanged) return prev;
            return { ...prev, ...contentMap.mission };
          });
        }
        if (contentMap.tracking) {
          setTrackingConfig(prev => {
            const hasChanged = JSON.stringify(prev) !== JSON.stringify({ ...prev, ...contentMap.tracking });
            if (!hasChanged) return prev;
            return { ...prev, ...contentMap.tracking };
          });
        }
        if (contentMap.financials) {
          setFinancialSettings(prev => {
            const hasChanged = JSON.stringify(prev) !== JSON.stringify({ ...prev, ...contentMap.financials });
            if (!hasChanged) return prev;
            return { ...prev, ...contentMap.financials };
          });
        }
        if (contentMap.founders) {
          setFounders(prev => {
            if (JSON.stringify(prev) === JSON.stringify(contentMap.founders)) return prev;
            return contentMap.founders;
          });
        }

        // Merge generic content
        setContent(prev => {
          const newContent = { ...prev };
          let changed = false;
          Object.keys(contentMap).forEach(key => {
            if (!['assets', 'mission', 'tracking', 'founders', 'financials'].includes(key)) {
              if (newContent[key as keyof typeof prev] !== contentMap[key]) {
                newContent[key as keyof typeof prev] = contentMap[key];
                changed = true;
              }
            }
          });
          return changed ? newContent : prev;
        });
      }, (err) => console.error("BrandContext brand_content error:", err))
    ];

    return () => unsubs.forEach(unsub => unsub());
  }, []);

  // User Specific Data Sync
  useEffect(() => {
    if (!isAuthReady) return;

    // Clear lists if no user
    if (!currentUser) {
      setOrders([]);
      setUserBookings([]);
      setAdminBookings([]);
      setAdminOrders([]);
      return;
    }

    const unsubs: (() => void)[] = [];

    // 1. Standard listeners: always run for any authenticated user (even admins)
    // to populate their personal userBookings and orders lists.
    const ordersCol = collection(db, 'orders');
    const bookingsCol = collection(db, 'bookings');

    let ordersByUid: Order[] = [];
    let ordersByEmail: Order[] = [];

    let bookingsByUid: ExpeditionBooking[] = [];
    let bookingsByClientEmail: ExpeditionBooking[] = [];
    let bookingsByUserEmail: ExpeditionBooking[] = [];

    const mergeAndSetOrders = () => {
      const combined = [...ordersByUid];
      console.log("Orders by UID:", ordersByUid);
      ordersByEmail.forEach(item => {
        if (!combined.some(c => c.id === item.id)) {
          combined.push(item);
        }
      });
      console.log("Combined orders:", combined);
      setOrders(combined.sort((a, b) => new Date(b.timestamp || b.createdAt || 0).getTime() - new Date(a.timestamp || a.createdAt || 0).getTime()));
    };

    const mergeAndSetBookings = () => {
      const combined = [...bookingsByUid];
      const addIfNew = (item: ExpeditionBooking) => {
        if (!combined.some(c => c.id === item.id)) {
          combined.push(item);
        }
      };
      bookingsByClientEmail.forEach(addIfNew);
      bookingsByUserEmail.forEach(addIfNew);
      setUserBookings(combined.sort((a, b) => new Date(b.timestamp || b.createdAt || 0).getTime() - new Date(a.timestamp || a.createdAt || 0).getTime()));
    };

    // Listen orders by uid
    unsubs.push(
      onSnapshot(query(ordersCol, where('userId', '==', currentUser.uid)), (snapshot) => {
        ordersByUid = snapshot.docs.map(d => ({ ...d.data(), id: d.id })) as Order[];
        mergeAndSetOrders();
      }, (err) => console.error("ordersByUid error:", err))
    );

    // Listen orders by email
    if (currentUser.email) {
      unsubs.push(
        onSnapshot(query(ordersCol, where('userEmail', '==', currentUser.email)), (snapshot) => {
          ordersByEmail = snapshot.docs.map(d => ({ ...d.data(), id: d.id })) as Order[];
          mergeAndSetOrders();
        }, (err) => console.error("ordersByEmail error:", err))
      );
    }

    // Listen bookings by uid
    unsubs.push(
      onSnapshot(query(bookingsCol, where('userId', '==', currentUser.uid)), (snapshot) => {
        bookingsByUid = snapshot.docs.map(d => ({ ...d.data(), id: d.id })) as ExpeditionBooking[];
        mergeAndSetBookings();
      }, (err) => console.error("bookingsByUid error:", err))
    );

    // Listen bookings by clientEmail
    if (currentUser.email) {
      unsubs.push(
        onSnapshot(query(bookingsCol, where('clientEmail', '==', currentUser.email)), (snapshot) => {
          bookingsByClientEmail = snapshot.docs.map(d => ({ ...d.data(), id: d.id })) as ExpeditionBooking[];
          mergeAndSetBookings();
        }, (err) => console.error("bookingsByClientEmail error:", err))
      );
      unsubs.push(
        onSnapshot(query(bookingsCol, where('userEmail', '==', currentUser.email)), (snapshot) => {
          bookingsByUserEmail = snapshot.docs.map(d => ({ ...d.data(), id: d.id })) as ExpeditionBooking[];
          mergeAndSetBookings();
        }, (err) => console.error("bookingsByUserEmail error:", err))
      );
    }

    // 2. Admin listeners: populate all bookings/orders if user is admin
    if (isAdmin) {
      unsubs.push(
        onSnapshot(collection(db, 'orders'), (snapshot) => {
          const data = snapshot.docs.map(d => ({ ...d.data(), id: d.id })) as Order[];
          console.log("Admin orders:", data);
          setAdminOrders(data.sort((a, b) => new Date(b.timestamp || b.createdAt || 0).getTime() - new Date(a.timestamp || a.createdAt || 0).getTime()));
        }, (err) => console.error("BrandContext admin orders error:", err)),
        onSnapshot(collection(db, 'bookings'), (snapshot) => {
          const data = snapshot.docs.map(d => ({ ...d.data(), id: d.id })) as ExpeditionBooking[];
          console.log("Admin bookings:", data);
          setAdminBookings(data.sort((a, b) => new Date(b.timestamp || b.createdAt || 0).getTime() - new Date(a.timestamp || a.createdAt || 0).getTime()));
        }, (err) => console.error("BrandContext admin bookings error:", err))
      );
    } else {
      setAdminBookings([]);
      setAdminOrders([]);
    }

    return () => unsubs.forEach(unsub => unsub());
  }, [currentUser, isAdmin, isAuthReady]);

  useEffect(() => {
    console.log("Orders:", orders);
  }, [orders]);
  useEffect(() => {
    console.log("User:", currentUser);
  }, [currentUser]);

  useEffect(() => {
    console.log("isAdmin:", isAdmin);
  }, [isAdmin]);

  const login = async () => {
    // Handled by UserContext
  };

  const logout = async () => {
    // Handled by UserContext
  };

  const updateAsset = async (key: string, value: string | boolean) => {
    console.log(`BrandContext: Updating asset ${key} to ${value}`);

    setAssets(prev => {
      const newAssets = { ...prev, [key]: value };
      console.log('BrandContext: New assets state:', newAssets);

      // Save to local storage immediately
      localStorage.setItem('ows_assets', JSON.stringify(newAssets));

      // Save to Firestore only if authenticated
      const saveToFirestore = async () => {
        if (!currentUser) {
          console.warn('BrandContext: Not signed in with Google. Changes saved locally but not to cloud.');
          return;
        }

        try {
          console.log('BrandContext: Saving assets to Firestore...');
          await setDoc(doc(db, 'brand_content', 'assets'), {
            key: 'assets',
            value: newAssets,
            updatedAt: new Date().toISOString()
          });
          await updateSyncStatus('brand-assets', 'synced');
          console.log('BrandContext: Assets saved to Firestore successfully');
        } catch (e) {
          console.error("BrandContext: Asset update failed in Firestore", e);
          if (e instanceof Error && e.message.includes('permission')) {
            handleFirestoreError(e, OperationType.WRITE, 'brand_content/assets');
          }
        }
      };

      saveToFirestore();
      return newAssets;
    });
  };

  const updateContent = async (key: string, value: string) => {
    const newContent = { ...content, [key]: value };
    setContent(newContent);
    localStorage.setItem('ows_content', JSON.stringify(newContent));
    try {
      await setDoc(doc(db, 'brand_content', key), { key, value });
      await updateSyncStatus('brand-content', 'synced');
    } catch (e) {
      console.error("BrandContext: Content update failed", e);
      if (e instanceof Error && e.message.includes('permission')) {
        handleFirestoreError(e, OperationType.WRITE, `brand_content/${key}`);
      }
    }
  };

  const bulkUpdateContent = async (updates: Record<string, any>) => {
    if (updates.mission) {
      setMission(updates.mission);
      localStorage.setItem('ows_mission', JSON.stringify(updates.mission));
    }
    if (updates.tracking) {
      setTrackingConfig(updates.tracking);
      localStorage.setItem('ows_tracking', JSON.stringify(updates.tracking));
    }

    const contentUpdates = { ...updates };
    delete contentUpdates.mission;
    delete contentUpdates.tracking;

    if (Object.keys(contentUpdates).length > 0) {
      const newContent = { ...content, ...contentUpdates };
      setContent(newContent);
      localStorage.setItem('ows_content', JSON.stringify(newContent));
    }

    try {
      for (const [key, value] of Object.entries(updates)) {
        await setDoc(doc(db, 'brand_content', key), { key, value });
      }
    } catch (e) {
      console.error("BrandContext: Bulk content update failed", e);
    }
  };

  const updateProduct = async (id: string, updates: Partial<Product>) => {
    try {
      await updateDoc(doc(db, 'products', id), updates);
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `products/${id}`);
    }
  };

  const addProduct = async (product: Product) => {
    try {
      await addDoc(collection(db, 'products'), product);
    } catch (e) {
      handleFirestoreError(e, OperationType.CREATE, 'products');
    }
  };

  const removeProduct = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'products', id));
    } catch (e) {
      handleFirestoreError(e, OperationType.DELETE, `products/${id}`);
    }
  };

  const updateStory = async (id: string, updates: Partial<Story>) => {
    try {
      await updateDoc(doc(db, 'blog_posts', id), updates);
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `blog_posts/${id}`);
    }
  };

  const addStory = async (story: Story) => {
    try {
      const payload = {
        ...story,
        author: story.author || currentUser?.displayName || 'OWS Team',
        date: story.date || new Date().toISOString()
      };
      await addDoc(collection(db, 'blog_posts'), payload);
    } catch (e) {
      handleFirestoreError(e, OperationType.CREATE, 'blog_posts');
    }
  };

  const removeStory = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'blog_posts', id));
    } catch (e) {
      handleFirestoreError(e, OperationType.DELETE, `blog_posts/${id}`);
    }
  };

  const updateFounder = async (id: string, updates: any) => {
    const newFounders = founders.map(f => f.id === id ? { ...f, ...updates } : f);
    setFounders(newFounders);
    try {
      await setDoc(doc(db, 'brand_content', 'founders'), { key: 'founders', value: newFounders });
    } catch (e) {
      console.error("BrandContext: Founder update failed", e);
    }
  };

  const addFounder = async (founder: any) => {
    const newFounders = [...founders, { ...founder, id: Date.now().toString() }];
    setFounders(newFounders);
    try {
      await setDoc(doc(db, 'brand_content', 'founders'), { key: 'founders', value: newFounders });
    } catch (e) {
      console.error("BrandContext: Founder creation failed", e);
    }
  };

  const removeFounder = async (id: string) => {
    const newFounders = founders.filter(f => String(f.id) !== String(id));
    setFounders(newFounders);
    try {
      await setDoc(doc(db, 'brand_content', 'founders'), { key: 'founders', value: newFounders });
    } catch (e) {
      console.error("BrandContext: Founder removal failed", e);
    }
  };

  const updateMembershipPlan = async (id: string, updates: Partial<MembershipPlan>) => {
    try {
      await setDoc(doc(db, 'products', id), updates, { merge: true });
    } catch (e) {
      console.error("BrandContext: Membership plan update failed", e);
    }
  };

  const addMembershipPlan = async (plan: MembershipPlan) => {
    try {
      await addDoc(collection(db, 'products'), { ...plan, category: 'membership' });
    } catch (e) {
      console.error("BrandContext: Membership plan creation failed", e);
    }
  };

  const removeMembershipPlan = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'products', id));
    } catch (e) {
      console.error("BrandContext: Membership plan removal failed", e);
    }
  };

  const updateMission = async (updates: Partial<BrandContextType['mission']>) => {
    const newMission = { ...mission, ...updates };
    setMission(newMission);
    localStorage.setItem('ows_mission', JSON.stringify(newMission));
    try {
      await setDoc(doc(db, 'brand_content', 'mission'), { key: 'mission', value: newMission });
    } catch (e) {
      console.error("BrandContext: Mission update failed", e);
    }
  };

  const updateExplorerTV = async (id: string, updates: any) => {
    try {
      await updateDoc(doc(db, 'blog_posts', id), updates);
    } catch (e) {
      console.error("BrandContext: Explorer TV update failed", e);
    }
  };

  const addExplorerTV = async (video: any) => {
    try {
      const payload = {
        ...video,
        category: 'Explorer TV',
        author: video.author || currentUser?.displayName || 'OWS Team',
        date: video.date || new Date().toISOString()
      };
      await addDoc(collection(db, 'blog_posts'), payload);
    } catch (e) {
      console.error("BrandContext: Explorer TV creation failed", e);
    }
  };

  const removeExplorerTV = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'blog_posts', id));
    } catch (e) {
      console.error("BrandContext: Explorer TV removal failed", e);
    }
  };

  const updateNightGallery = async (id: string, updates: any) => {
    try {
      await updateDoc(doc(db, 'blog_posts', id), updates);
    } catch (e) {
      console.error("BrandContext: Night Gallery update failed", e);
    }
  };

  const addNightGallery = async (item: any) => {
    try {
      const payload = {
        ...item,
        category: 'Night Gallery',
        author: item.author || currentUser?.displayName || 'OWS Team',
        date: item.date || new Date().toISOString()
      };
      await addDoc(collection(db, 'blog_posts'), payload);
    } catch (e) {
      console.error("BrandContext: Night Gallery creation failed", e);
    }
  };

  const removeNightGallery = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'blog_posts', id));
    } catch (e) {
      console.error("BrandContext: Night Gallery removal failed", e);
    }
  };

  const addMediaLibrary = async (item: any) => {
    try {
      const payload = {
        ...item,
        category: 'Media Library',
        author: item.author || currentUser?.displayName || 'OWS Team',
        date: item.date || new Date().toISOString()
      };
      await addDoc(collection(db, 'blog_posts'), payload);
    } catch (e) {
      console.error("BrandContext: Media Library creation failed", e);
    }
  };

  const removeMediaLibrary = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'blog_posts', id));
    } catch (e) {
      console.error("BrandContext: Media Library removal failed", e);
    }
  };

  const addCoachingPlan = async (plan: Partial<CoachingPlan>) => {
    try {
      await addDoc(collection(db, 'coaching_plans'), plan);
    } catch (e) {
      console.error("BrandContext: Failed to add coaching plan", e);
    }
  };

  const updateCoachingPlan = async (id: string, updates: Partial<CoachingPlan>) => {
    try {
      await setDoc(doc(db, 'coaching_plans', id), updates, { merge: true });
    } catch (e) {
      console.error("BrandContext: Failed to update coaching plan", e);
    }
  };

  const removeCoachingPlan = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'coaching_plans', id));
    } catch (e) {
      console.error("BrandContext: Failed to remove coaching plan", e);
    }
  };

  const updateTrackingConfig = async (updates: Partial<TrackingConfig>) => {
    const newConfig = { ...trackingConfig, ...updates };
    setTrackingConfig(newConfig);
    localStorage.setItem('ows_tracking', JSON.stringify(newConfig));
    try {
      await setDoc(doc(db, 'brand_content', 'tracking'), { key: 'tracking', value: newConfig });
    } catch (e) {
      console.error("BrandContext: Tracking config update failed", e);
    }
  };

  const updateFinancialSettings = async (updates: Partial<FinancialSettings>) => {
    const newSettings = { ...financialSettings, ...updates };
    setFinancialSettings(newSettings);
    localStorage.setItem('ows_financial', JSON.stringify(newSettings));
    try {
      await setDoc(doc(db, 'brand_content', 'financials'), { key: 'financials', value: newSettings });
    } catch (e) {
      console.error("BrandContext: Financial settings update failed", e);
    }
  };

  const addExpeditionStop = async (stop: any) => {
    try {
      await addDoc(collection(db, 'expedition_stops'), stop);
    } catch (e) {
      console.error("BrandContext: Expedition stop creation failed", e);
    }
  };

  const updateExpeditionStop = async (id: string, updates: Partial<ExpeditionStop>) => {
    try {
      await updateDoc(doc(db, 'expedition_stops', id), updates);
    } catch (e) {
      console.error("BrandContext: Expedition stop update failed", e);
    }
  };

  const removeExpeditionStop = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'expedition_stops', id));
    } catch (e) {
      console.error("BrandContext: Expedition stop removal failed", e);
    }
  };

  const addExpeditionSlot = async (slot: ExpeditionSlot) => {
    try {
      await addDoc(collection(db, 'expedition_slots'), slot);
    } catch (e) {
      console.error("BrandContext: Expedition slot creation failed", e);
      handleFirestoreError(e, OperationType.CREATE, 'expedition_slots');
    }
  };

  const updateExpeditionSlot = async (id: string, updates: Partial<ExpeditionSlot>) => {
    try {
      await updateDoc(doc(db, 'expedition_slots', id), updates);
    } catch (e) {
      console.error("BrandContext: Expedition slot update failed", e);
      handleFirestoreError(e, OperationType.UPDATE, `expedition_slots/${id}`);
    }
  };

  const removeExpeditionSlot = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'expedition_slots', id));
    } catch (e) {
      console.error("BrandContext: Expedition slot removal failed", e);
      handleFirestoreError(e, OperationType.DELETE, `expedition_slots/${id}`);
    }
  };

  const bookExpedition = async (slotId: string, details: { name: string, email: string, phone: string, waiverAccepted: boolean, status?: string, requiresGear?: boolean, intensityLevel?: string }) => {
    const authUid = currentUser?.uid || profile?.uid;
    const authEmail = currentUser?.email || profile?.email;
    const authName = currentUser?.displayName || profile?.displayName;

    const slot = expeditionSlots.find(s => s.id === slotId);
    if (!slot) throw new Error("Slot not found");

    const serviceName =
      slot.type === 'night-raid' ? 'Night Raid' :
        slot.type === 'coaching' ? 'Private Coaching' :
          slot.type === 'team-building' ? 'Team Building' :
            slot.type === 'baja' ? 'Baja Expedition' :
              slot.type === 'group-lesson' ? 'Group Lesson' :
                'Special Event';

    try {
      const newBookingRef = doc(collection(db, 'bookings'));
      const bookingId = newBookingRef.id;

      await setDoc(newBookingRef, {
        userId: authUid || 'guest',
        clientName: authName || details.name,
        clientEmail: authEmail || details.email,
        clientPhone: details.phone || 'N/A',
        userEmail: authEmail || details.email,
        userName: authName || details.name,
        userPhone: details.phone || 'N/A',
        serviceName,
        date: slot.date,
        time: slot.time,
        slotId: slotId,
        status: details.status || 'confirmed',
        paymentId: 'free_access',
        amountTotal: 0,
        createdAt: new Date().toISOString(),
        timestamp: new Date().toISOString(),
        waiverId: bookingId,
        waiverSigned: details.waiverAccepted === true,
        requiresGear: details.requiresGear === true,
        intensityLevel: details.intensityLevel || null,
        instructions: {
          meet: slot.exactMeetingLocation || slot.location || (serviceName === 'Night Raid' ? 'Toronto Islands' : 'Waterfront HQ'),
          expect: 'Initial safety briefing, physical alignment work, and technical maritime maneuvers.',
          bring: 'Valid PHOTO ID, water bottle, and a warm dry change of clothes.',
          clothes: 'Comfortable sports/athletic layers or neoprene wear appropriate for wind conditions.'
        }
      });

      if (details.status !== 'waitlist') {
        const slotRef = doc(db, 'expedition_slots', slotId);

        let increment = 1;
        if (serviceName === 'Team Building' || serviceName.includes('Corporate')) {
          increment = slot.capacity || 8;
        }

        await updateDoc(slotRef, {
          booked: (slot.booked || 0) + increment
        });
      }
    } catch (e) {
      console.error("Booking Error:", e);
      throw e;
    }
  };

  const cancelBooking = async (bookingId: string) => {
    try {
      const booking = userBookings.find(b => b.id === bookingId) || adminBookings.find(b => b.id === bookingId);
      if (booking) {
        const slot = expeditionSlots.find(s => s.id === booking.slotId);
        if (slot) {
          let decrement = 1;
          if (booking.serviceName === 'Team Building' || booking.serviceName?.includes('Corporate')) {
            decrement = slot.capacity || 8;
          }

          await updateDoc(doc(db, 'expedition_slots', booking.slotId), {
            booked: Math.max(0, (slot.booked || 0) - decrement)
          });
        }
      }
      await deleteDoc(doc(db, 'bookings', bookingId));
    } catch (e) {
      console.error("BrandContext: Booking cancellation failed", e);
    }
  };

  const addOrder = async (orderData: Omit<Order, 'id' | 'timestamp' | 'status'>) => {
    try {
      const payload = {
        ...orderData,
        timestamp: new Date().toISOString(),
        status: 'pending'
      };
      const docRef = await addDoc(collection(db, 'orders'), payload);

      const isVessel = orderData.type === 'vessel_reservation' || orderData.isVessel ||
        orderData.items?.some(i => i.name?.toLowerCase().includes('vessel') || i.name?.toLowerCase().includes('endurance'));

      if (isVessel && currentUser) {
        try {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            boardOrdered: true,
            orderDate: new Date().toISOString()
          });
        } catch (updateErr) {
          console.error("Failed to update user profile boardOrdered status:", updateErr);
        }
      }

      // Email dispatch bypassed - user gets UI notifications instead
      console.log(`[Bypassed Email] Order generated ${orderData.type}`);
    } catch (e) {
      console.error("BrandContext: Order creation failed", e);
    }
  };

  const updateOrder = async (id: string, updates: Partial<Order>) => {
    try {
      await updateDoc(doc(db, 'orders', id), updates);
    } catch (e) {
      console.error("BrandContext: Order update failed", e);
    }
  };

  const removeOrder = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'orders', id));
    } catch (e) {
      console.error("BrandContext: Order deletion failed", e);
    }
  };

  const [localMediaMap, setLocalMediaMap] = useState<Record<string, string>>({});

  // Load local media from IndexedDB on mount
  useEffect(() => {
    const loadLocalMedia = async () => {
      try {
        const allKeys = await keys();
        const mediaKeys = allKeys.filter(k => typeof k === 'string' && k.startsWith('ows-media-'));
        const newMap: Record<string, string> = {};

        for (const key of mediaKeys) {
          const blob = await get(key);
          if (blob instanceof Blob) {
            newMap[key as string] = URL.createObjectURL(blob);
          }
        }
        setLocalMediaMap(newMap);
      } catch (err) {
        console.error("BrandContext: Failed to load local media", err);
      }
    };
    loadLocalMedia();
  }, []);

  const [isUploading, setIsUploading] = useState(false);

  const resolveMediaUrl = React.useCallback((url: string) => {
    if (!url) return undefined;
    if (typeof url !== 'string') return url;

    if (url.startsWith('ows-media-')) {
      const resolved = localMediaMap[url];
      if (!resolved) {
        return undefined;
      }
      return resolved;
    }
    return url;
  }, [localMediaMap]);

  const uploadToLocal = async (file: File, onProgress?: (progress: number) => void) => {
    try {
      console.log(`Fallback: Uploading ${file.name} to local storage (IndexedDB)`);
      const id = `ows-media-${Date.now()}-${file.name.replace(/\s+/g, '_')}`;
      await set(id, file);
      const localUrl = URL.createObjectURL(file);

      // Update map immediately
      setLocalMediaMap(prev => ({ ...prev, [id]: localUrl }));

      if (onProgress) onProgress(100);
      return id;
    } catch (err) {
      console.error("Local upload failed:", err);
      throw new Error("Local storage upload failed. Your browser storage might be full.");
    }
  };

  const uploadToLocalServer = async (file: File): Promise<string> => {
    console.log("BrandContext: Attempting local server upload fallback...");
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch('/api/upload', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || 'Local upload failed');
    }

    const data = await response.json();
    console.log("BrandContext: Local upload success:", data.url);
    return data.url;
  };

  const uploadFile = async (file: File, path: string, onProgress?: (progress: number) => void) => {
    if (!currentUser) {
      // Allow local upload even if not logged in to Firebase if we want to be less "complicated"
      // but for now let's keep it restricted to admin intent
      console.warn("BrandContext: No Firebase user, attempting local upload...");
      try {
        setIsUploading(true);
        const url = await uploadToLocalServer(file);
        setIsUploading(false);
        return url;
      } catch (e) {
        setIsUploading(false);
        throw e;
      }
    }

    console.log(`BrandContext: Starting Firebase Storage upload for ${file.name} to ${path}`);
    setIsUploading(true);

    try {
      const safeName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
      const storageRef = ref(storage, `${path}/${safeName}`);

      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise<string>((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (onProgress) onProgress(progress);
          },
          async (error) => {
            console.error("BrandContext: Firebase Storage upload failed, trying local fallback...", error);
            try {
              const url = await uploadToLocalServer(file);
              resolve(url);
            } catch (fallbackErr) {
              console.error("BrandContext: Local fallback also failed.", fallbackErr);
              setIsUploading(false);
              reject(new Error("Cloud upload and Local fallback both failed. check connection."));
            }
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              setIsUploading(false);
              resolve(downloadURL);
            } catch (err: any) {
              // Try local fallback as a second chance
              try {
                const url = await uploadToLocalServer(file);
                resolve(url);
              } catch (fallbackErr) {
                setIsUploading(false);
                reject(err);
              }
            }
          }
        );
      });
    } catch (err: any) {
      setIsUploading(false);
      throw err;
    }
  };

  const clearLocalMedia = async () => {
    try {
      const allKeys = await keys();
      const mediaKeys = allKeys.filter(k => typeof k === 'string' && k.startsWith('ows-media-'));
      for (const key of mediaKeys) {
        await del(key);
      }
      setLocalMediaMap({});
      console.log("Cleared all local media from IndexedDB");
    } catch (err) {
      console.error("Error clearing local media:", err);
    }
  };

  const resetToDefaults = () => {
    localStorage.clear();
    clearLocalMedia().then(() => {
      window.location.reload();
    });
  };

  const toggleEditMode = () => setIsEditMode(!isEditMode);

  // Force update to the new UX optimized heroSubtitle if the user has an old one in localStorage
  useEffect(() => {
    if (
      content.heroSubtitle === "Expedition-grade paddle boards & premium guided experiences. Built for extreme redundancy and true open-water exploration." ||
      content.heroSubtitle === "The world's first true all-terrain expedition SUP. Built for survival redundancy and long-range overlanding on the open water." ||
      content.heroSubtitle === "Redundancy for the Unknown." ||
      content.heroSubtitle === "The Open Water" ||
      content.heroSubtitle === "Survival Redundancy for the Unknown. Expedition-grade paddle boards & premium guided experiences for true open-water exploration." ||
      content.heroSubtitle === "Expedition paddleboarding for everyday adventurers, endurance athletes, and elite open-water explorers."
    ) {
      setContent(prev => ({
        ...prev,
        heroSubtitle: "The shallows are crowded. The deep is for the few. We are selecting 200 capable individuals to train as self-sufficient open-water operators. Will you be one of them?"
      }));
    }

    if (
      content.dreamQuote === "We forged Open Water Syndicate from a brutal 52km crossing that exposed the gap between casual gear and survival reality.\n\nYou don't belong in the shallows. OWS provides the industrial-grade hardware, coiled training, and elite community you need to safely push your limits, master endurance paddleboarding, and explore the open water with absolute confidence."
    ) {
      setContent(prev => ({
        ...prev,
        dreamQuote: "Anyone can play in the shallows. We are looking for the few who want to conquer the deep. We just need 300 members to train. Are you one of them?"
      }));
    }
  }, [content.heroSubtitle, content.dreamQuote]);

  return (
    <BrandContext.Provider value={{
      assets,
      content,
      products,
      stories,
      founders,
      expeditionSlots,
      loadingSlots,
      userBookings,
      orders,
      adminBookings,
      adminOrders,
      membershipPlans,
      coachingPlans,
      mission,
      explorerTV,
      nightGallery,
      mediaLibrary,
      expeditionStops,
      trackingConfig,
      financialSettings,
      isEditMode,
      currentUser,
      isAuthReady,
      login,
      logout,
      updateAsset,
      updateContent,
      updateProduct,
      addProduct,
      removeProduct,
      updateStory,
      addStory,
      removeStory,
      updateFounder,
      addFounder,
      removeFounder,
      addCoachingPlan,
      updateCoachingPlan,
      removeCoachingPlan,
      updateMembershipPlan,
      addMembershipPlan,
      removeMembershipPlan,
      updateMission,
      updateExplorerTV,
      addExplorerTV,
      removeExplorerTV,
      updateNightGallery,
      addNightGallery,
      removeNightGallery,
      addMediaLibrary,
      removeMediaLibrary,
      updateExpeditionStop,
      addExpeditionStop,
      removeExpeditionStop,
      updateTrackingConfig,
      updateFinancialSettings,
      addExpeditionSlot,
      updateExpeditionSlot,
      removeExpeditionSlot,
      bookExpedition,
      cancelBooking,
      addOrder,
      updateOrder,
      removeOrder,
      selectedConfiguration,
      setSelectedConfiguration,
      uploadFile,
      resolveMediaUrl,
      isUploading,
      resetToDefaults,
      clearLocalMedia,
      toggleEditMode,
      bulkUpdateContent,
      syncStatuses,
      updateSyncStatus
    }}>
      {children}
    </BrandContext.Provider>
  );
}

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};